import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SupabaseService } from '../services/supabaseService';
import { BattleRecord, War, Inventory } from '../types';
import { ChevronLeft, Swords, Clock, Play, Filter, Trophy, Skull, Crown, Activity } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const History: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const filterWarId = location.state?.warId;

    const [battles, setBattles] = useState<BattleRecord[]>([]);
    const [warContext, setWarContext] = useState<War | null>(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'victory' | 'defeat'>('all');

    useEffect(() => {
        const loadHistory = async () => {
            setLoading(true);
            try {
                // 1. Fetch Battles
                const allBattles = await SupabaseService.getBattleHistory();

                // 2. Fetch War Context if filtered
                if (filterWarId) {
                    setBattles(allBattles.filter(b => b.war_id === filterWarId));
                    const warData = await SupabaseService.getWarById(filterWarId);
                    setWarContext(warData);
                } else {
                    setBattles(allBattles);
                }
            } catch (err) {
                console.error("Failed to load history:", err);
            } finally {
                setLoading(false);
            }
        };
        loadHistory();
    }, [filterWarId]);

    // Derived Stats
    const totalBattles = battles.length;
    const victories = battles.filter(b => {
        if (!b.result) return false;
        const mySide = b.settings.side;
        return (b.result.winner === 'w' && mySide === 'white') || (b.result.winner === 'b' && mySide === 'black');
    }).length;
    const winRate = totalBattles > 0 ? Math.round((victories / totalBattles) * 100) : 0;

    const filteredBattles = battles.filter(b => {
        if (!b.result) return false;
        const mySide = b.settings.side;
        const isWin = (b.result.winner === 'w' && mySide === 'white') || (b.result.winner === 'b' && mySide === 'black');
        if (filter === 'victory') return isWin;
        if (filter === 'defeat') return !isWin && b.result.winner !== 'draw'; // Treating draw as non-win
        return true;
    });

    const getOutcome = (battle: BattleRecord) => {
        if (!battle.result) return { label: 'In Progress', color: 'text-zinc-500', bg: 'bg-zinc-900' };
        if (battle.result.winner === 'draw') return { label: 'Draw', color: 'text-zinc-400', bg: 'bg-zinc-800' };

        const mySide = battle.settings.side;
        const isWin = (battle.result.winner === 'w' && mySide === 'white') || (battle.result.winner === 'b' && mySide === 'black');

        return isWin
            ? { label: 'Victory', color: 'text-tudor-gold', bg: 'bg-tudor-gold/10', border: 'border-tudor-gold/30' }
            : { label: 'Defeat', color: 'text-red-500', bg: 'bg-red-900/10', border: 'border-red-900/30' };
    };

    const countLosses = (inv: Inventory) => Object.values(inv).reduce((a, b) => a + b, 0);

    return (
        <div className="min-h-screen bg-tudor-black text-white font-mono p-4 md:p-8 pt-28">
            <div className="max-w-5xl mx-auto space-y-8">

                {/* Header Section */}
                <header className="flex flex-col gap-6 animate-fade-in-up">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => filterWarId ? navigate('/campaigns') : navigate('/')}
                                className="group p-3 border border-zinc-700 hover:border-tudor-gold text-zinc-400 hover:text-tudor-gold transition-all rounded"
                            >
                                <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                            </button>
                            <div>
                                <h1 className="text-3xl md:text-4xl text-tudor-gold font-serif uppercase tracking-[0.15em]">
                                    {filterWarId ? 'Campaign Log' : 'Royal Archives'}
                                </h1>
                                <p className="text-zinc-500 text-sm tracking-widest uppercase">
                                    {filterWarId ? `Directives for Operation #${filterWarId.slice(0, 8)}` : 'Comprehensive Battle Records'}
                                </p>
                            </div>
                        </div>

                        {/* War Context Badge */}
                        {warContext && (
                            <div className="hidden md:flex flex-col items-end">
                                <div className={`px-4 py-1 border text-sm uppercase tracking-widest font-bold ${warContext.status === 'ACTIVE' ? 'border-green-800 text-green-500 bg-green-900/10' : 'border-zinc-700 text-zinc-500'}`}>
                                    {warContext.status}
                                </div>
                                <div className="text-xs text-zinc-600 mt-1">Started: {new Date(warContext.created_at).toLocaleDateString()}</div>
                            </div>
                        )}
                    </div>

                    {/* Stats Dashboard */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded md:col-span-1 flex flex-col justify-between group hover:border-zinc-600 transition-colors">
                            <div className="text-zinc-500 uppercase text-[10px] tracking-widest flex items-center gap-2"><Swords size={12} /> Total Engagements</div>
                            <div className="text-2xl text-white font-mono">{totalBattles}</div>
                        </div>
                        <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded md:col-span-1 flex flex-col justify-between group hover:border-tudor-gold/30 transition-colors">
                            <div className="text-zinc-500 uppercase text-[10px] tracking-widest flex items-center gap-2"><Trophy size={12} /> Victories</div>
                            <div className="text-2xl text-tudor-gold font-mono">{victories}</div>
                        </div>
                        <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded md:col-span-1 flex flex-col justify-between group hover:border-red-900/30 transition-colors">
                            <div className="text-zinc-500 uppercase text-[10px] tracking-widest flex items-center gap-2"><Skull size={12} /> Defeats</div>
                            <div className="text-2xl text-red-500 font-mono">{totalBattles - victories}</div>
                        </div>
                        <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded md:col-span-1 flex flex-col justify-between">
                            <div className="text-zinc-500 uppercase text-[10px] tracking-widest flex items-center gap-2"><Activity size={12} /> Win Rate</div>
                            <div className="text-2xl text-white font-mono">{winRate}%</div>
                        </div>
                    </div>
                </header>

                {/* Filters */}
                <div className="flex gap-2 border-b border-zinc-800 pb-4 sticky top-24 bg-tudor-black/95 backdrop-blur z-20">
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-6 py-2 text-xs uppercase tracking-widest border transition-all ${filter === 'all' ? 'bg-white text-black border-white' : 'bg-transparent text-zinc-500 border-zinc-800 hover:border-zinc-600'}`}
                    >
                        All Records
                    </button>
                    <button
                        onClick={() => setFilter('victory')}
                        className={`px-6 py-2 text-xs uppercase tracking-widest border transition-all ${filter === 'victory' ? 'bg-tudor-gold text-black border-tudor-gold' : 'bg-transparent text-zinc-500 border-zinc-800 hover:text-tudor-gold hover:border-tudor-gold/30'}`}
                    >
                        Victories
                    </button>
                    <button
                        onClick={() => setFilter('defeat')}
                        className={`px-6 py-2 text-xs uppercase tracking-widest border transition-all ${filter === 'defeat' ? 'bg-red-600 text-black border-red-600' : 'bg-transparent text-zinc-500 border-zinc-800 hover:text-red-500 hover:border-red-500/30'}`}
                    >
                        Defeats
                    </button>
                </div>

                {/* Battle List */}
                <div className="space-y-4 min-h-[400px]">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4 opacity-50 animate-pulse">
                            <Swords size={48} className="text-tudor-gold" />
                            <p className="text-tudor-gold uppercase tracking-widest text-xs">Accessing Royal Archives...</p>
                        </div>
                    ) : filteredBattles.length === 0 ? (
                        <div className="py-20 text-center border border-dashed border-zinc-800 rounded bg-zinc-900/20">
                            <p className="text-zinc-500 text-sm">No records match the current criteria.</p>
                        </div>
                    ) : (
                        filteredBattles.map((battle) => {
                            const outcome = getOutcome(battle);
                            const casualtyCount = battle.result?.casualties ? countLosses(battle.result.casualties) : 0;
                            const enemyCasualtyCount = battle.result?.enemy_casualties ? countLosses(battle.result.enemy_casualties) : 0;

                            return (
                                <div key={battle.id} className={`group relative p-6 bg-zinc-900/40 border ${outcome.border || 'border-zinc-800'} rounded hover:bg-zinc-900 hover:border-opacity-100 transition-all flex flex-col md:flex-row items-center gap-6 overflow-hidden`}>

                                    {/* Decoration Line */}
                                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${outcome.color.replace('text-', 'bg-')}`}></div>

                                    {/* Status Icon */}
                                    <div className={`shrink-0 w-12 h-12 flex items-center justify-center rounded-full ${outcome.bg}`}>
                                        {outcome.label === 'Victory' ? <Crown size={20} className={outcome.color} /> : <Skull size={20} className={outcome.color} />}
                                    </div>

                                    {/* Main Info */}
                                    <div className="flex-grow text-center md:text-left">
                                        <div className="flex items-center justify-center md:justify-start gap-3 mb-1">
                                            <h3 className={`font-serif text-xl tracking-widest uppercase ${outcome.color}`}>{outcome.label}</h3>
                                            <span className="text-zinc-600">vs</span>
                                            <span className="text-white font-bold tracking-wide uppercase">{battle.settings.opponent_type === 'ai' ? 'Tactical AI' : 'Opponent'}</span>
                                        </div>
                                        <div className="text-xs text-zinc-500 flex items-center justify-center md:justify-start gap-4 uppercase tracking-widest">
                                            <span className="flex items-center gap-1"><Clock size={12} /> {new Date(battle.created_at).toLocaleDateString()}</span>
                                            <span className="w-1 h-1 bg-zinc-700 rounded-full"></span>
                                            <span>Turn {Math.floor(Math.random() * 40 + 10)}</span> {/* Placeholder for Turns if not in DB */}
                                        </div>
                                    </div>

                                    {/* K/D Stats */}
                                    {battle.result && (
                                        <div className="flex gap-8 text-center border-l border-zinc-800 pl-8 pr-4">
                                            <div>
                                                <div className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Losses</div>
                                                <div className="text-lg text-red-400 font-mono">-{casualtyCount}</div>
                                            </div>
                                            <div>
                                                <div className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Kills</div>
                                                <div className="text-lg text-green-400 font-mono">+{enemyCasualtyCount}</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action */}
                                    <button
                                        onClick={() => navigate(`/replay/${battle.id}`)}
                                        className="w-full md:w-auto px-6 py-3 border border-zinc-700 text-zinc-400 hover:text-white hover:border-white hover:bg-white/5 uppercase tracking-widest text-xs font-bold transition-all flex items-center justify-center gap-2 rounded"
                                    >
                                        <Play size={14} /> Replay
                                    </button>

                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default History;
